import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (data.status === "expired" || attempts.current > 10) {
          setStatus("failed");
          return;
        }
      } catch {
        if (attempts.current > 10) { setStatus("failed"); return; }
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center">
      {status === "checking" && (
        <div className="animate-fade-up" data-testid="payment-checking">
          <Loader2 className="w-14 h-14 animate-spin text-primary mx-auto mb-6" />
          <h1 className="font-display text-2xl font-bold mb-2">Confirming your payment…</h1>
          <p className="text-muted-foreground">This only takes a moment.</p>
        </div>
      )}
      {status === "paid" && (
        <div className="animate-fade-up" data-testid="payment-success">
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-9 h-9 text-accent" />
          </div>
          <h1 className="font-display text-3xl font-extrabold mb-3">Payment successful</h1>
          <p className="text-muted-foreground mb-8">
            Your purchase is confirmed and now available in your dashboard.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate("/dashboard")} data-testid="go-to-dashboard-button">
              Go to dashboard <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Button variant="outline" asChild><Link to="/resources">Browse more</Link></Button>
          </div>
        </div>
      )}
      {status === "failed" && (
        <div className="animate-fade-up" data-testid="payment-failed">
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-9 h-9 text-destructive" />
          </div>
          <h1 className="font-display text-2xl font-bold mb-3">We couldn't confirm your payment</h1>
          <p className="text-muted-foreground mb-8">
            If you were charged, it will appear shortly. Otherwise please try again.
          </p>
          <Button asChild><Link to="/pricing">Back to pricing</Link></Button>
        </div>
      )}
    </div>
  );
}
